import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1767466069523-c24e095f2b56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxwZWFjZWZ1bCUyMHJlc2lkZW50aWFsJTIwaG9tZSUyMGV4dGVyaW9yJTIwd2FybSUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4NTE3OTIxNXww&ixlib=rb-4.1.0&q=85";

const lines = ["Safe housing.", "Restored dignity.", "A fresh start."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.5 } },
};
const lineVariant = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = ({ onApply, onLearn }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Warm residential home at sunset" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#12241B]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#12241B]/80 via-transparent to-[#12241B]/30" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-20 lg:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-xs font-sans uppercase tracking-[0.3em] font-semibold text-primary mb-6"
          data-testid="hero-overline"
        >
          Transitional Living Services
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-serif text-background leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-8xl max-w-4xl"
          data-testid="hero-heading"
        >
          {lines.map((line) => (
            <span key={line} className="block overflow-hidden">
              <motion.span variants={lineVariant} className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-8 text-background/85 font-sans text-lg max-w-xl leading-relaxed"
        >
          Supportive shared housing that empowers veterans, individuals in recovery, and adults
          transitioning from homelessness to rebuild stability and move toward independence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Button
            data-testid="hero-apply-btn"
            onClick={onApply}
            className="rounded-full bg-primary text-primary-foreground hover:bg-background hover:text-foreground transition-colors duration-300 px-8 h-12 text-base"
          >
            Start Your Application
          </Button>
          <button
            data-testid="hero-learn-btn"
            onClick={onLearn}
            className="rounded-full border border-background/60 text-background hover:bg-background hover:text-foreground transition-colors duration-300 px-8 h-12 text-base font-medium"
          >
            Our Mission
          </button>
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 right-8 z-10 text-background/70"
      >
        <ArrowDown size={22} />
      </motion.div>
    </section>
  );
};
